import { motion } from "framer-motion";
import { BadgeCheck, FileSearch, ShieldCheck, Zap } from "lucide-react";

const creds = [
  { icon: ShieldCheck, title: "Licensed & Insured", sub: "Florida HI License #HI19148 · General Liability + E&O" },
  { icon: FileSearch, title: "Same-Day Reports", sub: "Photo-documented digital reports within 24 hours" },
  { icon: Zap, title: "Thermal Imaging Included", sub: "Infrared moisture & electrical scans on every full inspection" },
  { icon: BadgeCheck, title: "Carrier-Ready", sub: "Citizens & private insurer accepted 4-Point / Wind Mit forms" },
];

export const About = () => (
  <section id="about" className="bg-sand py-28 sm:py-36">
    <div className="max-w-4xl mx-auto px-5 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="font-mono text-xs tracking-[0.25em] text-terra uppercase font-semibold mb-4">/ Your Inspector</div>
        <h2 className="font-display font-bold tracking-tight text-obsidian text-2xl sm:text-3xl lg:text-4xl leading-tight max-w-2xl">
          A second set of trained eyes on the biggest purchase of your life.
        </h2>
        <p className="mt-6 text-slate-600 text-base sm:text-lg leading-relaxed max-w-2xl">
          Safe Point Inspections LLC is an independent, licensed and insured Florida home
          inspection company serving South Florida. We work for you — never the
          seller, never the insurer — and every finding is documented with
          photos, thermal imaging, and plain-English explanations.
        </p>

        <div className="mt-7 border-l-2 border-terra pl-5" data-testid="about-inspector-signature">
          <div className="font-display font-bold text-obsidian text-lg tracking-tight">Andre Rodriguez</div>
          <div className="font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase mt-1">
            Owner &amp; Licensed Home Inspector · FL HI19148
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href="https://www.myfloridalicense.com/wl11.asp"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-license-verify-link"
            className="bg-obsidian text-sand rounded-xl px-6 py-5 shadow-xl transition-transform duration-300 hover:scale-105"
          >
            <div className="font-mono text-[10px] tracking-[0.25em] text-sun uppercase">State of Florida</div>
            <div className="font-display font-extrabold text-xl mt-1">License #HI19148</div>
            <div className="font-mono text-[9px] tracking-[0.2em] text-sand/50 uppercase mt-1.5">Tap to verify ↗</div>
          </a>
          <a
            href="/proof-of-insurance.pdf"
            download
            data-testid="about-insurance-download"
            className="bg-paper border border-sandline text-obsidian rounded-xl px-5 py-4 shadow-sm flex items-center gap-3 transition-transform duration-300 hover:scale-105"
          >
            <ShieldCheck size={20} className="text-terra shrink-0" />
            <div>
              <div className="font-mono text-[9px] tracking-[0.25em] text-slate-500 uppercase">E&amp;O + General Liability</div>
              <div className="font-display font-extrabold text-base leading-tight">Proof of Insurance ↓</div>
            </div>
          </a>
          <a
            href="/license-hi19148.pdf"
            download
            data-testid="about-license-download"
            className="bg-paper border border-sandline text-obsidian rounded-xl px-5 py-4 shadow-sm flex items-center gap-3 transition-transform duration-300 hover:scale-105"
          >
            <BadgeCheck size={20} className="text-terra shrink-0" />
            <div>
              <div className="font-mono text-[9px] tracking-[0.25em] text-slate-500 uppercase">DBPR Home Inspector</div>
              <div className="font-display font-extrabold text-base leading-tight">License Document ↓</div>
            </div>
          </a>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          {creds.map((c) => (
            <div key={c.title} className="flex gap-3.5" data-testid={`about-cred-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <c.icon size={20} className="text-terra shrink-0 mt-1" />
              <div>
                <div className="font-display font-bold text-obsidian">{c.title}</div>
                <div className="text-sm text-slate-500 mt-1 leading-relaxed">{c.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);
